import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import getTestConversations from 'redux/actions/Common/getTestConversations';
import createTestConversation from 'redux/actions/Common/createTestConversation';
import getTestConversationResponse from 'redux/actions/Common/getTestConversationResponse';
import testLllPrompt from 'redux/actions/Common/testLllPrompt';
import addTestConversationMessage from 'redux/actions/Common/addTestConversationMessage';
import processRagQueryForLlmContext from 'redux/actions/Common/processRagQueryForLlmContext';
import { capitalizeText } from 'utils';

const EMPTY_STEP_NON_CONTEXT_CONVERSATION = {
  prompt: '',
  output: '',
  messageId: null,
  generatingOutput: false,
};

const EMPTY_STEP = {
  plannerPrompt: '',
  userInput: '',
  generatedPlan: '',
  generatedPlanError: false,
  pineconeData: '',
  finalPrompt: '',
  output: '',
  messageId: null,
  generatingPlan: false,
  executingPlan: false,
  executingOutput: false,
};

const isValidJson = (str) => {
  if (!str.trim()) return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

const buildStepFromMessageNonContextConversation = (msg) => ({
  plannerPrompt:
    msg?.stepsMessageMapping?.step_1_req?.gptMessage?.content || '',
  userInput: msg?.userQuery || '',
  generatingPlan: false,
  generatedPlan: (() => {
    const content = msg?.stepsMessageMapping?.step_2_req?.gptMessage?.content;
    if (!content) return '';
    return typeof content === 'string'
      ? content
      : JSON.stringify(content, null, 2);
  })(),
  messageId: msg?.id || null,
  generatingOutput: false,
});

const buildStepFromMessage = (msg) => ({
  plannerPrompt:
    msg?.stepsMessageMapping?.step_1_req?.gptMessage?.content || '',
  userInput: msg?.userQuery || '',
  generatedPlan: (() => {
    const content = msg?.stepsMessageMapping?.step_2_req?.gptMessage?.content;
    if (!content) return '';
    return typeof content === 'string'
      ? content
      : JSON.stringify(content, null, 2);
  })(),
  generatedPlanError: false,
  pineconeData:
    msg?.stepsMessageMapping?.step_3_req?.contextContainer?.context || '',
  finalPrompt: msg?.stepsMessageMapping?.step_3_req?.gptMessage?.content || '',
  output:
    msg?.finalConversationMessages?.find(
      (m) => m?.gptMessage?.role === 'assistant',
    )?.gptMessage?.content || '',
  messageId: msg?.id || null,
  generatingPlan: false,
  executingPlan: false,
  executingOutput: false,
  finalResponse:
    msg?.finalConversationMessages?.find(
      (m) => m?.gptMessage?.role === 'assistant',
    )?.gptMessage?.content || '',
  promptPlaceholders: msg?.promptPlaceholders || {},
  gptFileIds: [
    ...(msg?.stepsMessageMapping?.step_3_req?.customFields?.fileUrls || []),
  ].map((file) => ({
    ...file,
    selected: msg?.finalConversationMessages
      ? msg?.finalConversationMessages?.[0]?.gptFileIds?.includes(
          file?.gptFileId,
        )
      : true,
  })),
});

const RagTestingView = ({ selectedOrganization, selectedDeal }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { conversations, fetching } = useSelector(
    (state) => state.testConversations,
  );
  const { creating } = useSelector((state) => state.createTestConversation);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
  const [newConvTitle, setNewConvTitle] = useState('');
  const [showNewConvInput, setShowNewConvInput] = useState(false);
  const [testConversationType, setTestConversationType] = useState(
    'expert_conversation',
  );
  const [promptType, setPromptType] = useState('nba_actions_inbound_email');
  const [indexProvider, setIndexProvider] = useState('pinecone');

  useEffect(() => {
    dispatch(getTestConversations());
  }, []);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    const msgs = conv?.messages;
    if (Array.isArray(msgs) && msgs.length > 0) {
      if (conv.testConversationType === 'non_context_conversation') {
        setSteps(msgs.map(buildStepFromMessageNonContextConversation));
      } else {
        setSteps(msgs.map(buildStepFromMessage));
      }
    } else {
      if (conv.testConversationType === 'non_context_conversation') {
        setSteps([{ ...EMPTY_STEP_NON_CONTEXT_CONVERSATION }]);
      } else {
        setSteps([{ ...EMPTY_STEP }]);
      }
    }
  };

  const handleCreateConversation = () => {
    if (!newConvTitle.trim()) return;
    dispatch(
      createTestConversation(
        {
          title: newConvTitle.trim(),
          orgId: selectedOrganization?.id,
          dealId: selectedDeal?.id,
          testConversationType: testConversationType,
          promptType:
            testConversationType === 'non_context_conversation'
              ? promptType
              : null,
          vectorDbProvider: indexProvider,
        },
        (created) => {
          setNewConvTitle('');
          setShowNewConvInput(false);
          handleSelectConversation(created);
        },
      ),
    );
  };

  const updateStep = (index, patch) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  // Ensures the step has a backend message slot; calls callback with the messageId.
  const ensureMessageId = (index, currentMessageId, callback, onError) => {
    if (currentMessageId) {
      callback(currentMessageId);
      return;
    }
    dispatch(
      addTestConversationMessage(
        selectedConversation.id,
        {
          orgId: selectedOrganization?.id,
          dealId: selectedDeal?.id,
        },
        (msg) => {
          const msgId = msg?.id;
          updateStep(index, { messageId: msgId });
          callback(msgId);
        },
        onError,
      ),
    );
  };

  const handleGeneratePlan = (index) => {
    if (!selectedConversation?.id) return;
    const step = steps[index];
    updateStep(index, { generatingPlan: true });

    ensureMessageId(
      index,
      step.messageId,
      (messageId) => {
        dispatch(
          testLllPrompt(
            {
              prompt: step.plannerPrompt,
              initialUserQuery: step.userInput,
              testConversationId: selectedConversation.id,
              testConversationMessageId: step.messageId,
              step: 'step_1_req',
              placeholder: selectedConversation?.messages?.[0]?.inputKey
                ? {
                    ...(step.promptPlaceholders || {}),
                    '<%>userQuery<%>': step.userInput,
                    '<%>userEmail<%>': step.userInput,
                    [selectedConversation?.messages?.[0]
                      ?.inputKey]: step.userInput,
                  }
                : {
                    ...(step.promptPlaceholders || {}),
                    '<%>userQuery<%>': step.userInput,
                    '<%>userEmail<%>': step.userInput,
                  },
              promptSections: null,
            },
            (data) => {
              // console.log(data);
              updateStep(index, {
                generatedPlan: data?.response || '',
                generatingPlan: false,
              });
              dispatch({
                type: 'UPDATING_TEST_CONVERSATION_DONE',
                payload: {
                  ...selectedConversation,
                  messages: data?.messages || [],
                },
              });
            },
            () => updateStep(index, { generatingPlan: false }),
          ),
        );
      },
      () => updateStep(index, { generatingPlan: false }),
    );
  };

  const handleExecutePlan = (index) => {
    if (!selectedConversation?.id) return;
    const step = steps[index];

    if (!isValidJson(step.generatedPlan)) {
      updateStep(index, { generatedPlanError: true });
      return;
    }

    let actions;
    try {
      actions = JSON.parse(step.generatedPlan);
    } catch {
      updateStep(index, { generatedPlanError: true });
      return;
    }

    updateStep(index, { executingPlan: true, generatedPlanError: false });

    ensureMessageId(
      index,
      step.messageId,
      (messageId) => {
        dispatch(
          processRagQueryForLlmContext(
            {
              orgId: selectedOrganization?.id,
              dealId: selectedDeal?.id,
              actions: Array.isArray(actions) ? actions : actions?.actions,
              initialQuery: step.userInput,
              testConversationId: selectedConversation.id,
              ragNamespaceMapForFE:
                selectedOrganization?.ragNamespaceMapForFE || {},
              messageId,
              step: 'step_2_req',
              provider: selectedConversation?.vectorDbProvider || 'pinecone',
            },
            (data) => {
              console.log(data);
              updateStep(index, {
                pineconeData:
                  data?.context?.context ||
                  (typeof data?.context === 'string' ? data.context : '') ||
                  '',
                finalPrompt: data?.response || '',
                executingPlan: false,
              });
              dispatch({
                type: 'UPDATING_TEST_CONVERSATION_DONE',
                payload: {
                  ...selectedConversation,
                  messages: data?.messages || [],
                },
              });
            },
            () => updateStep(index, { executingPlan: false }),
          ),
        );
      },
      () => updateStep(index, { executingPlan: false }),
    );
  };

  const handleExecuteOutput = (index) => {
    if (!selectedConversation?.id) return;
    const step = steps[index];
    updateStep(index, { executingOutput: true });

    dispatch(
      getTestConversationResponse(
        selectedConversation.id,
        {
          userInput: step.finalPrompt,
          messageId: step.messageId,
          gptFileIds:
            step.gptFileIds
              ?.filter((file) => file.selected)
              ?.map((file) => file?.gptFileId) || null,
        },
        (data) => {
          updateStep(index, {
            output:
              data?.aiResponse ||
              data?.message?.finalConversationMessages?.at(-1)?.content ||
              '',
            executingOutput: false,
          });
          dispatch({
            type: 'UPDATING_TEST_CONVERSATION_DONE',
            payload: {
              ...selectedConversation,
              messages:
                selectedConversation?.messages?.map((msg, i) =>
                  msg?.id === data?.message?.id
                    ? { ...msg, ...data?.message }
                    : msg,
                ) || [],
            },
          });
        },
        () => updateStep(index, { executingOutput: false }),
      ),
    );
  };

  const handleAddStep = () => {
    if (!selectedConversation?.id) {
      setSteps((prev) => [...prev, { ...EMPTY_STEP }]);
      return;
    }
    const initialPlannerPrompt = steps[0]?.plannerPrompt || '';
    dispatch(
      addTestConversationMessage(
        selectedConversation.id,
        {
          orgId: selectedOrganization?.id,
          dealId: selectedDeal?.id,
        },
        (msg) => {
          setSteps((prev) => [
            ...prev,
            {
              ...EMPTY_STEP,
              plannerPrompt: initialPlannerPrompt,
              messageId: msg?.id || null,
            },
          ]);
          dispatch({
            type: 'UPDATING_TEST_CONVERSATION_DONE',
            payload: {
              ...selectedConversation,
              messages: [...(selectedConversation?.messages || []), msg],
            },
          });
        },
        () => {
          setSteps((prev) => [
            ...prev,
            { ...EMPTY_STEP, plannerPrompt: initialPlannerPrompt },
          ]);
        },
      ),
    );
  };

  return (
    <Box display="flex" height="100%" overflow="hidden">
      {/* Left Sidebar */}
      <Box
        sx={{
          width: 260,
          minWidth: 260,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          minHeight: 'calc(100vh - 164px)',
          maxHeight: 'calc(100vh - 164px)',
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            All Conversations
          </Typography>
          <Tooltip title="New Conversation">
            <IconButton
              size="small"
              onClick={() => setShowNewConvInput((v) => !v)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {showNewConvInput && (
          <Box
            sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Conversation title"
              value={newConvTitle}
              onChange={(e) => setNewConvTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateConversation()}
              sx={{ mb: 1 }}
            />
            <TextField
              select
              size="small"
              fullWidth
              label="Conversation Type"
              value={testConversationType}
              onChange={(e) => setTestConversationType(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              {[
                'expert_conversation',
                'inbound_email_conversation',
                'non_context_conversation',
              ].map((type) => (
                <MenuItem key={type} value={type}>
                  {type
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </MenuItem>
              ))}
            </TextField>
            {testConversationType === 'non_context_conversation' && (
              <TextField
                select
                size="small"
                fullWidth
                label="Prompt Type"
                value={promptType}
                onChange={(e) => setPromptType(e.target.value)}
              >
                {[
                  'nba_actions_inbound_email',
                  'nba_actions_call_and_meeting',
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type
                      .split('_')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(' ')}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {testConversationType !== 'non_context_conversation' ? (
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
              >
                <FormLabel id="indexProvider">Index Provider</FormLabel>
                <Select
                  labelId="indexprovider-select-label"
                  value={indexProvider || ''}
                  onChange={(e) => {
                    setIndexProvider(e.target.value);
                  }}
                >
                  <MenuItem value="pinecone">Pinecone</MenuItem>
                  <MenuItem value="qdrant">Qdrant</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            <Button
              variant="contained"
              size="small"
              fullWidth
              disabled={creating || !newConvTitle.trim()}
              onClick={handleCreateConversation}
            >
              {creating ? <CircularProgress size={16} /> : 'Create'}
            </Button>
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          {fetching ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : !Array.isArray(conversations) || conversations.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: 'center' }}
            >
              No conversations yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {conversations.map((conv) => (
                <ListItem key={conv?.id} disablePadding>
                  <ListItemButton
                    selected={selectedConversation?.id === conv?.id}
                    onClick={() => handleSelectConversation(conv)}
                    sx={{ py: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Typography variant="body2">
                            {conv?.title || conv?.id || 'Untitled'} --{' '}
                            {conv?.dealId ? 'Deal' : 'Global'}
                          </Typography>
                        </Box>
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                      }}
                      secondary={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Typography variant="caption">
                            {conv?.createdOn
                              ? new Date(conv.createdOn).toLocaleDateString()
                              : null}
                          </Typography>
                          {conv?.vectorDbProvider ? (
                            <Typography variant="caption">
                              {conv?.vectorDbProvider}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          minHeight: 'calc(100vh - 164px)',
          maxHeight: 'calc(100vh - 164px)',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2,
          },
        }}
      >
        {!selectedConversation ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body1" color="text.secondary">
              Select a conversation or create a new one.
            </Typography>
          </Box>
        ) : (
          <Box>
            {steps.map((step, index) => (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    Step {index + 1}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* Planner Prompt */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      mb={0.5}
                      display="block"
                    >
                      Planner Prompt{' '}
                      {selectedConversation?.promptType
                        ? `(
                      ${capitalizeText(
                        selectedConversation?.promptType?.split('_')?.join(' '),
                      )}
                      )`
                        : null}
                    </Typography>
                    <TextField
                      multiline
                      minRows={4}
                      maxRows={8}
                      fullWidth
                      size="small"
                      value={step.plannerPrompt}
                      onChange={(e) =>
                        updateStep(index, { plannerPrompt: e.target.value })
                      }
                      placeholder="Planner prompt will appear here..."
                    />
                  </Box>
                  {selectedConversation?.testConversationType ===
                  'non_context_conversation' ? (
                    <>
                      {/* User Input + Generate Plan */}
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          mb={0.5}
                          display="block"
                        >
                          User input
                        </Typography>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={4}
                            value={step.userInput}
                            onChange={(e) =>
                              updateStep(index, { userInput: e.target.value })
                            }
                            placeholder="Enter user input..."
                            onKeyDown={(e) =>
                              e.key === 'Enter' && handleGeneratePlan(index)
                            }
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleGeneratePlan(index)}
                            disabled={
                              step?.generatingPlan || !step?.userInput?.trim()
                            }
                            sx={{ whiteSpace: 'nowrap', minWidth: 130 }}
                          >
                            {step?.generatingPlan ? (
                              <CircularProgress size={16} />
                            ) : (
                              'Execute'
                            )}
                          </Button>
                        </Box>
                      </Box>
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={0.5}
                        >
                          <Typography
                            variant="caption"
                            color={
                              step?.generatedPlanError
                                ? 'error'
                                : 'text.secondary'
                            }
                          >
                            Generated Output
                            {step?.generatedPlanError ? ' — invalid JSON' : ''}
                          </Typography>
                          {/* <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleExecutePlan(index)}
                            disabled={
                              step?.executingPlan ||
                              !step?.generatedPlan?.trim()
                            }
                            sx={{ minWidth: 120 }}
                          >
                            {step?.executingPlan ? (
                              <CircularProgress size={14} />
                            ) : (
                              'Execute plan'
                            )}
                          </Button> */}
                        </Box>
                        <TextField
                          multiline
                          minRows={4}
                          fullWidth
                          size="small"
                          value={step?.generatedPlan}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateStep(index, {
                              generatedPlan: val,
                              generatedPlanError: val.trim()
                                ? !isValidJson(val)
                                : false,
                            });
                          }}
                          placeholder="Generated plan (JSON) will appear here..."
                          error={step?.generatedPlanError}
                          helperText={
                            step?.generatedPlanError ? 'Must be valid JSON' : ''
                          }
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* User Input + Generate Plan */}
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          mb={0.5}
                          display="block"
                        >
                          User input
                        </Typography>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={4}
                            value={step.userInput}
                            onChange={(e) =>
                              updateStep(index, { userInput: e.target.value })
                            }
                            placeholder="Enter user input..."
                            onKeyDown={(e) =>
                              e.key === 'Enter' && handleGeneratePlan(index)
                            }
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleGeneratePlan(index)}
                            disabled={
                              step?.generatingPlan || !step?.userInput?.trim()
                            }
                            sx={{ whiteSpace: 'nowrap', minWidth: 130 }}
                          >
                            {step?.generatingPlan ? (
                              <CircularProgress size={16} />
                            ) : (
                              'Generate Plan'
                            )}
                          </Button>
                        </Box>
                      </Box>

                      {/* Generated Plan */}
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={0.5}
                        >
                          <Typography
                            variant="caption"
                            color={
                              step?.generatedPlanError
                                ? 'error'
                                : 'text.secondary'
                            }
                          >
                            Generate Plan
                            {step?.generatedPlanError ? ' — invalid JSON' : ''}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleExecutePlan(index)}
                            disabled={
                              step?.executingPlan ||
                              !step?.generatedPlan?.trim()
                            }
                            sx={{ minWidth: 120 }}
                          >
                            {step?.executingPlan ? (
                              <CircularProgress size={14} />
                            ) : (
                              'Execute plan'
                            )}
                          </Button>
                        </Box>
                        <TextField
                          multiline
                          minRows={4}
                          fullWidth
                          size="small"
                          value={step?.generatedPlan}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateStep(index, {
                              generatedPlan: val,
                              generatedPlanError: val.trim()
                                ? !isValidJson(val)
                                : false,
                            });
                          }}
                          placeholder="Generated plan (JSON) will appear here..."
                          error={step?.generatedPlanError}
                          helperText={
                            step?.generatedPlanError ? 'Must be valid JSON' : ''
                          }
                        />
                      </Box>

                      {/* Pinecone fetched data */}
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          mb={0.5}
                          display="block"
                        >
                          Pinecone fetched data
                        </Typography>
                        <TextField
                          multiline
                          minRows={3}
                          fullWidth
                          size="small"
                          value={step?.pineconeData}
                          onChange={(e) =>
                            updateStep(index, { pineconeData: e.target.value })
                          }
                          placeholder="Pinecone / web search data will appear here..."
                        />
                      </Box>

                      {/* Fetched Files */}
                      {step?.gptFileIds && step?.gptFileIds.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            mb={0.5}
                            display="block"
                          >
                            Fetched Files (Selected files will go into the final
                            prompt execution to generate output)
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="column"
                            gap={0.5}
                            mb={1}
                          >
                            {step.gptFileIds.map((file, i) => (
                              <Box
                                key={i}
                                display="flex"
                                alignItems="center"
                                gap={1}
                              >
                                <Checkbox
                                  size="small"
                                  checked={file.selected}
                                  onChange={(e) => {
                                    updateStep(index, {
                                      gptFileIds: step.gptFileIds.map(
                                        (f, idx) =>
                                          idx === i
                                            ? {
                                                ...f,
                                                selected: e.target.checked,
                                              }
                                            : f,
                                      ),
                                    });
                                  }}
                                  sx={{ p: 0.5 }}
                                />
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={file?.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  color="primary"
                                  sx={{
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                    width: 'fit-content',
                                  }}
                                >
                                  {file?.fileName || file?.gptFileId}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Final Prompt */}
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={0.5}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Final prompt
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            onClick={() => handleExecuteOutput(index)}
                            disabled={
                              step?.executingOutput ||
                              !step?.finalPrompt?.trim()
                            }
                            sx={{ minWidth: 130 }}
                          >
                            {step?.executingOutput ? (
                              <CircularProgress size={14} />
                            ) : (
                              'Execute output'
                            )}
                          </Button>
                        </Box>
                        <TextField
                          multiline
                          minRows={4}
                          fullWidth
                          size="small"
                          value={step.finalPrompt}
                          onChange={(e) =>
                            updateStep(index, { finalPrompt: e.target.value })
                          }
                          placeholder="Final prompt will appear here..."
                        />
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={0.5}
                          mt={1}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Final Output
                          </Typography>
                        </Box>
                        {step.output ? (
                          <Box
                            sx={{
                              bgcolor: '#fffde7',
                              border: '1px solid',
                              borderColor: '#f9a825',
                              borderRadius: 1,
                              p: 1.5,
                              minHeight: 80,
                              maxHeight: 300,
                              overflowY: 'auto',
                              whiteSpace: 'pre-wrap',
                              // fontSize: 13,
                            }}
                          >
                            {step.output}
                          </Box>
                        ) : null}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))}

            {/* Add Next Step */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              sx={{ mb: 3 }}
            >
              Next Step
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RagTestingView;
